/**
 * Pure, defensive view-model for a single dropdown result row (GS-02).
 *
 * The backend serializers are compact and cannot be verified until the backend merges, so every
 * field is treated as possibly-absent: a missing field is omitted from the meta line, never rendered
 * as `undefined`. Kept framework-free so the per-type shaping is unit-tested.
 */
import {ResultItem} from './types'

export type ThumbShape = 'circle' | 'square' | 'icon'

export interface RowView {
  title: string
  meta: string[]
  thumbUrl?: string
  thumbShape: ThumbShape
}

/** Short relative date for forum rows (deterministic — `now` is injectable for tests). */
export const relativeDate = (iso?: string, now: number = Date.now()): string => {
  if (!iso) return ''
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return ''
  const diff = Math.max(0, now - t)
  const min = 60000
  const hour = 3600000
  const day = 86400000
  if (diff < min) return 'just now'
  if (diff < hour) {
    const m = Math.floor(diff / min)
    return `${m} minute${m > 1 ? 's' : ''} ago`
  }
  if (diff < day) {
    const h = Math.floor(diff / hour)
    return `${h} hour${h > 1 ? 's' : ''} ago`
  }
  if (diff < 30 * day) {
    const d = Math.floor(diff / day)
    return `${d} day${d > 1 ? 's' : ''} ago`
  }
  return new Date(t).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})
}

const shortDate = (iso?: string): string => {
  if (!iso) return ''
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return ''
  return new Date(t).toLocaleDateString('en-US', {month: 'short', day: 'numeric'})
}

/** Experience date range, tolerating a missing / single / identical endpoint. */
export const formatDateRange = (start?: string, end?: string): string => {
  const s = shortDate(start)
  const e = shortDate(end)
  if (s && e) return s === e ? s : `${s} – ${e}`
  return s || e || ''
}

/** Expertise rate, e.g. "$250 / hour". Omitted when absent. */
export const formatRate = (rate?: number | string): string => {
  if (rate === undefined || rate === null || rate === '') return ''
  return `$${rate} / hour`
}

/** Non-empty strings only — drops any absent meta part. */
const compact = (parts: (string | undefined | null | false)[]): string[] =>
  parts.filter((p): p is string => typeof p === 'string' && p.trim().length > 0)

export const describeRow = (item: ResultItem, now?: number): RowView => {
  switch (item.type) {
    case 'member':
      return {
        title: item.fullName || item.name || 'Member',
        meta: compact([item.location]),
        thumbUrl: item.avatar || item.image,
        thumbShape: 'circle',
      }
    case 'experience':
      return {
        title: item.title || 'Experience',
        meta: compact([item.location, formatDateRange(item.startDate, item.endDate)]),
        thumbUrl: item.coverImage || item.image,
        thumbShape: 'square',
      }
    case 'forum':
      return {
        title: item.title || 'Forum',
        meta: compact([item.authorName, relativeDate(item.createdAt, now)]),
        thumbShape: 'icon',
      }
    case 'mastermind_circle':
      return {
        title: item.title || 'Mastermind Circle',
        meta: compact([
          item.hostName,
          item.memberCount !== undefined && item.memberCount !== null
            ? `${item.memberCount} member${item.memberCount === 1 ? '' : 's'}`
            : undefined,
        ]),
        thumbShape: 'icon',
      }
    case 'expertise': {
      // Expert scope: title = company_name, meta shows the number of published services and
      // the partner address. Falls back to the legacy service-shape fields (title/expertName/
      // rate) if a stale response lands here.
      const title = item.companyName || item.title || item.expertName || 'Expert'
      const count = item.expertiseCount
      const countText =
        count !== undefined && count !== null
          ? `${count} service${count === 1 ? '' : 's'}`
          : undefined
      return {
        title,
        meta: compact([countText, item.address, formatRate(item.rate)]),
        thumbUrl: item.logo,
        thumbShape: item.logo ? 'square' : 'icon',
      }
    }
    default:
      return {title: '', meta: [], thumbShape: 'icon'}
  }
}
